/**
 * Typed client for the BEAM assistant-as-universal-action-router surface.
 *
 * Routes (beam-backend src/api/assistantRouter.ts — requireAuth + withTenant):
 *   POST /api/assistant/act   { message, context? }  → AssistantActionResult
 *   GET  /api/org/users                               → OrgUser[]
 *
 * The `assistantAct` envelope type is declared INLINE here (mirroring the backend
 * envelope) on purpose: per the build contract (§8) it must NOT be added to
 * @beam/api-types, to avoid the Docker-build boundary (beam-backend mirrors
 * @beam/api-types locally and the dep breaks its image build).
 *
 * Bearer token is attached automatically by src/api/client.ts.
 * Scope is derived from the JWT server-side — never sent in the body
 * (mobile CLAUDE.md invariant 2). NEVER imports @smartbox/* (invariant 1).
 */

import { get, post } from './client';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// AssistantActionResult — the ONE envelope mobile consumes from POST /api/assistant/act.
// Mirrors the backend shape from docs/assistant-action-router-design.md §2.
// Declared inline (not in @beam/api-types) by design — see module doc.
// ---------------------------------------------------------------------------

/** Entity kinds the router can deep-link to after an action. */
export const EntityType = z.enum([
  'estimate',
  'expense',
  'task',
  'schedule_event',
  'customer',
  'job',
  'batch',
]);
export type EntityType = z.infer<typeof EntityType>;

/** Deep-link target returned for kind:'action' (and sometimes kind:'answer'). */
export const AssistantTarget = z.object({
  type: EntityType,
  id: z.string(),
});
export type AssistantTarget = z.infer<typeof AssistantTarget>;

/**
 * The result envelope. `kind` selects how the UI responds:
 *  - 'action'        → an entity was created/updated; show summary + navigate to target.
 *  - 'answer'        → a Q&A reply in `content`; show it inline.
 *  - 'clarification' → the model needs more info (`clarification`); keep the dock open.
 *  - 'needs_owner'   → an owner-gated action (`reason`); explain + offer to route, never execute.
 */
export const AssistantActionResult = z.object({
  kind: z.enum(['action', 'answer', 'clarification', 'needs_owner']),
  summary: z.string(),
  target: AssistantTarget.optional(),
  content: z.string().optional(),
  clarification: z.string().optional(),
  reason: z.string().optional(),
  /** Guided-intake progress hint for a clarification: which step of how many (spec 3). */
  progress: z.object({ step: z.number(), total: z.number() }).optional(),
});
export type AssistantActionResult = z.infer<typeof AssistantActionResult>;

/** One prior conversation turn, oldest→newest, sent so a follow-up keeps context. */
export interface AssistantTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Optional screen context the dock passes so the router can resolve references. */
export interface AssistantActContext {
  /** The current Expo Router pathname (e.g. '/(app)/estimates/123'). */
  route?: string;
  /** The focused entity id, when a detail screen has one. */
  entityId?: string;
  /**
   * Recent conversation turns preceding this message so the router can resolve follow-ups and
   * replies to its own clarification questions (fixes "loses context after one message"). The
   * backend bounds this; keep it to the last several turns.
   */
  history?: AssistantTurn[];
}

/**
 * Submit a natural-language message to the universal action router.
 * Classifies + dispatches server-side through existing domain services and
 * returns the action/answer/clarification/needs_owner envelope.
 */
export async function assistantAct(
  message: string,
  context?: AssistantActContext,
): Promise<AssistantActionResult> {
  const raw = await post<unknown>('/api/assistant/act', {
    message,
    ...(context ? { context } : {}),
  });
  return AssistantActionResult.parse(raw);
}

// ---------------------------------------------------------------------------
// Org users — GET /api/org/users (assignee picker + client-side display).
// ---------------------------------------------------------------------------

export const OrgUser = z.object({
  id: z.string(),
  fullName: z.string(),
  role: z.string(),
  isActive: z.boolean(),
});
export type OrgUser = z.infer<typeof OrgUser>;

/** List the org's users (for assignee pickers and name display). */
export async function listOrgUsers(): Promise<OrgUser[]> {
  const raw = await get<unknown>('/api/org/users');
  return (raw as unknown[]).map((item) => OrgUser.parse(item));
}
